// src/pages/productos/GestionPrecios.js — POS-PRICE-LISTS-001A
// Extiende gestión existente. SSOT: producto.listas_precios + listasPreciosService.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaPercent,
  FaEdit,
  FaFileExport,
  FaFileExcel,
  FaFileAlt,
  FaPrint,
  FaSyncAlt,
  FaStore,
  FaCheckSquare,
  FaSquare
} from 'react-icons/fa';

import productosService from '../../services/productos.service';
import categoriasService from '../../services/categorias.service';
import proveedoresService from '../../services/proveedores.service';
import listasPreciosService from '../../services/listas-precios.service';
import { useAuth } from '../../contexts/AuthContext';

import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Table from '../../components/common/Table';
import ListasPreciosModal from '../../components/modules/productos/ListasPreciosModal';
import ListaPreciosImprimir from '../../components/modules/productos/ListaPreciosImprimir';
import ListaExpresPanel from '../../components/modules/productos/ListaExpresPanel';

import { formatCurrency } from '../../utils/format';
import { numberOrZero } from '../../utils/numberInput';
import useEmpresaConfig from '../../hooks/useEmpresaConfig';
import { etiquetaLista, LISTA_PRECIO_KEYS } from '../../utils/listasPreciosLabels';
import {
  buildCatalogoCsv,
  downloadTextFile,
  exportCatalogoExcel,
  uniqueMarcas
} from '../../utils/catalogoComercialExport';
import { CENTRO_COMERCIAL_MODULES } from '../centro-comercial/modulesRegistry';

const GestionPrecios = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { empresaConfig } = useEmpresaConfig();

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
  const [marcaSeleccionada, setMarcaSeleccionada] = useState('');
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState('');
  const [listaExport, setListaExport] = useState('mayorista');
  const [incluirStockExport, setIncluirStockExport] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [modalAbierto, setModalAbierto] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [mostrarHub, setMostrarHub] = useState(() => searchParams.get('hub') === '1');
  const [mostrarVistaImpresion, setMostrarVistaImpresion] = useState(
    () => searchParams.get('export') === '1'
  );
  const [mostrarListaExpres, setMostrarListaExpres] = useState(
    () => searchParams.get('expres') === '1'
  );

  const [mostrarActualizacionMasiva, setMostrarActualizacionMasiva] = useState(false);
  const [procesandoMasivo, setProcesandoMasivo] = useState(false);
  const [configuracionMasiva, setConfiguracionMasiva] = useState({
    tipo_actualizacion: 'porcentaje',
    valor: '',
    lista_precio: 'todas',
    categoria_id: '',
    motivo: '',
    base_calculo: 'precio_venta',
    tipo_redondeo: 'sin_redondeo'
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [productosData, categoriasData, proveedoresData] = await Promise.all([
        productosService.obtenerTodos(),
        categoriasService.obtenerTodos(),
        proveedoresService.obtenerTodos().catch(() => [])
      ]);

      const productosConListas = (productosData || []).map((producto) => ({
        ...producto,
        listas_precios: producto.listas_precios || {
          mayorista: producto.precio_venta || 0,
          interior: producto.precio_venta || 0,
          posadas: producto.precio_venta || 0
        }
      }));

      setProductos(productosConListas);
      setCategorias(categoriasData || []);
      setProveedores(proveedoresData || []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const marcasDisponibles = useMemo(() => uniqueMarcas(productos), [productos]);

  const productosFiltrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return productos.filter((producto) => {
      const coincideTermino =
        !term ||
        String(producto.nombre || '')
          .toLowerCase()
          .includes(term) ||
        String(producto.codigo || '')
          .toLowerCase()
          .includes(term);

      const coincideCategoria =
        !categoriaSeleccionada || producto.categoria_id === categoriaSeleccionada;

      const marca = String(producto.marca || producto.marca_nombre || '').trim();
      const coincideMarca = !marcaSeleccionada || marca === marcaSeleccionada;

      const coincideProveedor =
        !proveedorSeleccionado ||
        String(producto.proveedor_id || '') === String(proveedorSeleccionado);

      return coincideTermino && coincideCategoria && coincideMarca && coincideProveedor;
    });
  }, [
    productos,
    searchTerm,
    categoriaSeleccionada,
    marcaSeleccionada,
    proveedorSeleccionado
  ]);

  const allFilteredSelected =
    productosFiltrados.length > 0 &&
    productosFiltrados.every((p) => selectedIds.has(p.id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        productosFiltrados.forEach((p) => next.delete(p.id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      productosFiltrados.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const stats = useMemo(() => {
    return {
      totalProductos: productos.length,
      filtrados: productosFiltrados.length,
      seleccionados: selectedIds.size,
      sinPrecioCosto: productos.filter((p) => !p.precio_costo || p.precio_costo === 0).length
    };
  }, [productos, productosFiltrados, selectedIds]);

  const abrirModalEdicion = (producto) => {
    setProductoSeleccionado(producto);
    setModalAbierto(true);
  };

  const handleProductoActualizado = async (productoActualizado) => {
    setProductos((prevProductos) =>
      prevProductos.map((p) => {
        if (p.id !== productoActualizado.id) return p;
        return {
          ...p,
          precio_costo: productoActualizado.precio_costo,
          precio_venta: productoActualizado.precio_venta,
          listas_precios: productoActualizado.listas_precios || {
            mayorista: productoActualizado.precio_venta || 0,
            interior: productoActualizado.precio_venta || 0,
            posadas: productoActualizado.precio_venta || 0
          }
        };
      })
    );
    setTimeout(() => cargarDatos(), 800);
  };

  const ejecutarActualizacionMasiva = async () => {
    if (numberOrZero(configuracionMasiva.valor) === 0) {
      toast.warning('El valor de actualización no puede ser 0');
      return;
    }
    if (!configuracionMasiva.motivo.trim()) {
      toast.warning('Por favor indica el motivo de la actualización');
      return;
    }

    try {
      setProcesandoMasivo(true);
      const payload = {
        ...configuracionMasiva,
        valor: numberOrZero(configuracionMasiva.valor),
        usuario_id: currentUser?.uid
      };

      if (selectedIds.size > 0) {
        payload.producto_ids = Array.from(selectedIds);
      }

      const resultado = await listasPreciosService.actualizacionMasiva(payload);
      toast.success(resultado.message || 'Actualización masiva completada');
      await cargarDatos();
      setMostrarActualizacionMasiva(false);
      setConfiguracionMasiva({
        tipo_actualizacion: 'porcentaje',
        valor: '',
        lista_precio: 'todas',
        categoria_id: '',
        motivo: '',
        base_calculo: 'precio_venta',
        tipo_redondeo: 'sin_redondeo'
      });
    } catch (error) {
      console.error('Error en actualización masiva:', error);
      toast.error('Error al ejecutar actualización masiva');
    } finally {
      setProcesandoMasivo(false);
    }
  };

  const productosParaExport =
    selectedIds.size > 0
      ? productosFiltrados.filter((p) => selectedIds.has(p.id))
      : productosFiltrados;

  const exportarCsv = () => {
    try {
      const file = buildCatalogoCsv({
        productos: productosParaExport,
        categorias,
        empresaConfig,
        listaKey: listaExport,
        incluirStock: incluirStockExport
      });
      downloadTextFile(file);
      toast.success('CSV exportado');
    } catch (error) {
      console.error(error);
      toast.error('Error al exportar CSV');
    }
  };

  const exportarExcel = () => {
    try {
      exportCatalogoExcel({
        productos: productosParaExport,
        categorias,
        empresaConfig,
        listaKey: listaExport,
        incluirStock: incluirStockExport
      });
      toast.success('Excel exportado');
    } catch (error) {
      console.error(error);
      toast.error('Error al exportar Excel');
    }
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <button
            type="button"
            onClick={toggleSelectAllFiltered}
            className="flex items-center gap-2 text-e0e3-foreground"
            title={allFilteredSelected ? 'Quitar selección' : 'Seleccionar todos (filtro)'}
          >
            {allFilteredSelected ? <FaCheckSquare /> : <FaSquare />}
            <span className="sr-only">Seleccionar</span>
          </button>
        ),
        accessor: 'sel',
        cell: (row) => (
          <input
            type="checkbox"
            checked={selectedIds.has(row.id)}
            onChange={() => toggleSelect(row.id)}
            className="w-5 h-5"
            aria-label={`Seleccionar ${row.nombre}`}
          />
        )
      },
      {
        header: 'Producto',
        accessor: 'nombre',
        cell: (row) => (
          <div>
            <div className="font-medium">{row.nombre}</div>
            <div className="text-sm text-e0e3-muted">{row.codigo}</div>
          </div>
        )
      },
      {
        header: 'Costo',
        accessor: 'precio_costo',
        cell: (row) => (
          <span className="font-medium">{formatCurrency(row.precio_costo || 0)}</span>
        )
      },
      ...LISTA_PRECIO_KEYS.map((key) => ({
        header: etiquetaLista(empresaConfig, key),
        accessor: `precio_${key}`,
        cell: (row) => {
          const precio = row.listas_precios?.[key] || row.precio_venta || 0;
          const margen =
            row.precio_costo > 0
              ? (((precio - row.precio_costo) / row.precio_costo) * 100).toFixed(1)
              : 0;
          return (
            <div>
              <div className="font-medium">{formatCurrency(precio)}</div>
              <div className={`text-xs ${margen < 20 ? 'text-red-500' : 'text-e0e3-muted'}`}>
                {margen}%
              </div>
            </div>
          );
        }
      })),
      {
        header: 'Acciones',
        accessor: 'acciones',
        cell: (row) => (
          <button
            type="button"
            onClick={() => abrirModalEdicion(row)}
            className="text-e0e3-accent-soft hover:underline inline-flex items-center gap-1 min-h-[40px]"
            title="Editar precios"
          >
            <FaEdit /> Editar
          </button>
        )
      }
    ],
    [empresaConfig, selectedIds, allFilteredSelected, productosFiltrados]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">
            Listas de precios
          </h1>
          <p className="text-e0e3-muted mt-1">
            Gestión profesional de listas · motor existente sin cambios
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            color="primary"
            onClick={() => {
              setMostrarVistaImpresion(false);
              setMostrarListaExpres(true);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('expres', '1');
                next.delete('export');
                return next;
              });
            }}
            className="min-h-[48px]"
          >
            Lista Exprés
          </Button>
          <Button
            color="secondary"
            onClick={() => navigate('/centro-comercial')}
            icon={<FaStore />}
            className="min-h-[48px]"
          >
            Centro Comercial
          </Button>
          <Button
            color="secondary"
            onClick={() => navigate('/productos')}
            icon={<FaArrowLeft />}
            className="min-h-[48px]"
          >
            Volver
          </Button>
        </div>
      </div>

      {mostrarHub && (
        <Card className="border border-e0e3-accent/30">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-e0e3-foreground">Centro Comercial</h2>
                <p className="text-sm text-e0e3-muted">
                  Atajos a las herramientas comerciales (vista embebida · `?hub=1`)
                </p>
              </div>
              <Button color="secondary" size="sm" onClick={() => navigate('/centro-comercial')}>
                Abrir home
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {CENTRO_COMERCIAL_MODULES.map((mod) => {
                const active = mod.status === 'active';
                return (
                  <div
                    key={mod.id}
                    className={`rounded-lg border p-4 ${
                      active ? 'border-e0e3-accent/40 bg-white/[0.03]' : 'border-white/10 opacity-75'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-e0e3-foreground">{mod.label}</h3>
                      <span className="text-xs text-e0e3-muted">
                        {active ? 'Disponible' : 'Próximamente'}
                      </span>
                    </div>
                    <p className="text-sm text-e0e3-muted mt-1">{mod.description}</p>
                    {active ? (
                      <button
                        type="button"
                        className="mt-3 text-sm text-e0e3-accent-soft hover:underline min-h-[40px]"
                        onClick={() => {
                          setMostrarHub(false);
                          if (
                            mod.path &&
                            !String(mod.path).startsWith('/productos/precios')
                          ) {
                            navigate(mod.path);
                            return;
                          }
                          if (String(mod.path || '').includes('export=1')) {
                            setMostrarListaExpres(false);
                            setMostrarVistaImpresion(true);
                            return;
                          }
                          if (String(mod.path || '').includes('expres=1')) {
                            setMostrarVistaImpresion(false);
                            setMostrarListaExpres(true);
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('expres', '1');
                              next.delete('export');
                              return next;
                            });
                            return;
                          }
                          setMostrarListaExpres(false);
                        }}
                      >
                        Abrir
                      </button>
                    ) : (
                      <p className="mt-3 text-xs text-e0e3-muted">Próximamente</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {mostrarListaExpres ? (
        <ListaExpresPanel
          productos={productos}
          categorias={categorias}
          proveedores={proveedores}
          empresaConfig={empresaConfig}
          onClose={() => {
            setMostrarListaExpres(false);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete('expres');
              next.delete('step');
              next.delete('template');
              return next;
            });
          }}
        />
      ) : null}

      {!mostrarListaExpres && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-blue-500/30 bg-blue-500/10">
          <div className="text-center py-2">
            <div className="text-2xl font-bold text-blue-400">{stats.totalProductos}</div>
            <div className="text-sm text-e0e3-muted">Total</div>
          </div>
        </Card>
        <Card className="border border-emerald-500/30 bg-emerald-500/10">
          <div className="text-center py-2">
            <div className="text-2xl font-bold text-emerald-400">{stats.filtrados}</div>
            <div className="text-sm text-e0e3-muted">Filtrados</div>
          </div>
        </Card>
        <Card className="border border-amber-500/30 bg-amber-500/10">
          <div className="text-center py-2">
            <div className="text-2xl font-bold text-amber-400">{stats.seleccionados}</div>
            <div className="text-sm text-e0e3-muted">Seleccionados</div>
          </div>
        </Card>
        <Card className="border border-red-500/30 bg-red-500/10">
          <div className="text-center py-2">
            <div className="text-2xl font-bold text-red-400">{stats.sinPrecioCosto}</div>
            <div className="text-sm text-e0e3-muted">Sin costo</div>
          </div>
        </Card>
      </div>

      {mostrarActualizacionMasiva && (
        <Card className="border border-amber-500/30 bg-amber-500/10">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">
                Edición masiva de listas
                {selectedIds.size > 0
                  ? ` · ${selectedIds.size} seleccionados`
                  : ' · todos (según categoría)'}
              </h3>
              <button
                type="button"
                onClick={() => setMostrarActualizacionMasiva(false)}
                className="text-e0e3-muted hover:text-e0e3-foreground text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">Tipo</label>
                <select
                  value={configuracionMasiva.tipo_actualizacion}
                  onChange={(e) =>
                    setConfiguracionMasiva({
                      ...configuracionMasiva,
                      tipo_actualizacion: e.target.value
                    })
                  }
                  className="nexo-field"
                >
                  <option value="porcentaje">Porcentaje %</option>
                  <option value="monto_fijo">Monto Fijo $</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">Valor</label>
                <input
                  type="number"
                  value={configuracionMasiva.valor}
                  onChange={(e) =>
                    setConfiguracionMasiva({ ...configuracionMasiva, valor: e.target.value })
                  }
                  className="nexo-field"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">
                  Calcular desde
                </label>
                <select
                  value={configuracionMasiva.base_calculo}
                  onChange={(e) =>
                    setConfiguracionMasiva({
                      ...configuracionMasiva,
                      base_calculo: e.target.value
                    })
                  }
                  className="nexo-field"
                >
                  <option value="precio_venta">Precio de venta actual</option>
                  <option value="precio_costo">Precio de costo</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">Redondeo</label>
                <select
                  value={configuracionMasiva.tipo_redondeo}
                  onChange={(e) =>
                    setConfiguracionMasiva({
                      ...configuracionMasiva,
                      tipo_redondeo: e.target.value
                    })
                  }
                  className="nexo-field"
                >
                  <option value="sin_redondeo">Sin redondeo</option>
                  <option value="arriba">Arriba</option>
                  <option value="abajo">Abajo</option>
                  <option value="multiplo_5">Múltiplo de 5</option>
                  <option value="multiplo_10">Múltiplo de 10</option>
                  <option value="centena">Centena</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">Lista</label>
                <select
                  value={configuracionMasiva.lista_precio}
                  onChange={(e) =>
                    setConfiguracionMasiva({
                      ...configuracionMasiva,
                      lista_precio: e.target.value
                    })
                  }
                  className="nexo-field"
                >
                  <option value="todas">Todas las listas</option>
                  {LISTA_PRECIO_KEYS.map((key) => (
                    <option key={key} value={key}>
                      Solo {etiquetaLista(empresaConfig, key)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-e0e3-muted mb-1">
                  Categoría (si no hay selección)
                </label>
                <select
                  value={configuracionMasiva.categoria_id}
                  onChange={(e) =>
                    setConfiguracionMasiva({
                      ...configuracionMasiva,
                      categoria_id: e.target.value
                    })
                  }
                  className="nexo-field"
                  disabled={selectedIds.size > 0}
                >
                  <option value="">Todas</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-e0e3-muted mb-1">Motivo</label>
              <input
                type="text"
                value={configuracionMasiva.motivo}
                onChange={(e) =>
                  setConfiguracionMasiva({ ...configuracionMasiva, motivo: e.target.value })
                }
                className="nexo-field"
                placeholder="Ej: Ajuste por inflación"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button color="secondary" onClick={() => setMostrarActualizacionMasiva(false)}>
                Cancelar
              </Button>
              <Button
                color="warning"
                onClick={ejecutarActualizacionMasiva}
                disabled={numberOrZero(configuracionMasiva.valor) === 0 || procesandoMasivo}
                loading={procesandoMasivo}
                className="min-h-[48px]"
              >
                {procesandoMasivo ? 'Procesando…' : 'Aplicar'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Buscar por nombre o código…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="nexo-field min-h-[48px]"
            />
            <select
              value={categoriaSeleccionada}
              onChange={(e) => setCategoriaSeleccionada(e.target.value)}
              className="nexo-field min-h-[48px]"
            >
              <option value="">Todas las categorías</option>
              {categorias.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>
            <select
              value={marcaSeleccionada}
              onChange={(e) => setMarcaSeleccionada(e.target.value)}
              className="nexo-field min-h-[48px]"
            >
              <option value="">
                {marcasDisponibles.length ? 'Todas las marcas' : 'Sin marcas cargadas'}
              </option>
              {marcasDisponibles.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={proveedorSeleccionado}
              onChange={(e) => setProveedorSeleccionado(e.target.value)}
              className="nexo-field min-h-[48px]"
            >
              <option value="">Todos los proveedores</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={listaExport}
                onChange={(e) => setListaExport(e.target.value)}
                className="nexo-field min-h-[48px] min-w-[10rem]"
                title="Lista para exportar"
              >
                {LISTA_PRECIO_KEYS.map((key) => (
                  <option key={key} value={key}>
                    Exportar: {etiquetaLista(empresaConfig, key)}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-e0e3-muted min-h-[48px]">
                <input
                  type="checkbox"
                  checked={incluirStockExport}
                  onChange={(e) => setIncluirStockExport(e.target.checked)}
                />
                Incluir stock en Excel/CSV
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                color="warning"
                icon={<FaPercent />}
                onClick={() => setMostrarActualizacionMasiva(true)}
                className="min-h-[48px]"
              >
                Edición masiva
              </Button>
              <Button
                color="info"
                icon={<FaPrint />}
                onClick={() => setMostrarVistaImpresion(true)}
                className="min-h-[48px]"
              >
                Vista previa
              </Button>
              <Button
                color="success"
                icon={<FaFileExport />}
                onClick={() => setMostrarVistaImpresion(true)}
                className="min-h-[48px]"
              >
                PDF
              </Button>
              <Button
                color="success"
                icon={<FaFileExcel />}
                onClick={exportarExcel}
                className="min-h-[48px]"
              >
                Excel
              </Button>
              <Button
                color="secondary"
                icon={<FaFileAlt />}
                onClick={exportarCsv}
                className="min-h-[48px]"
              >
                CSV
              </Button>
              <Button
                color="secondary"
                icon={<FaSyncAlt />}
                onClick={cargarDatos}
                className="min-h-[48px]"
              >
                Actualizar
              </Button>
            </div>
          </div>

          <p className="text-xs text-e0e3-muted">
            Selección: {selectedIds.size} · Exporta selección o, si no hay, el filtro actual ·{' '}
            <button
              type="button"
              className="text-e0e3-accent-soft hover:underline"
              onClick={() => navigate('/centro-comercial')}
            >
              Ir al Centro Comercial
            </button>
          </p>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : productosFiltrados.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-e0e3-muted">
              {searchTerm || categoriaSeleccionada || marcaSeleccionada || proveedorSeleccionado
                ? 'No se encontraron productos con los filtros aplicados'
                : 'No hay productos registrados'}
            </p>
          </div>
        ) : (
          <Table
            columns={columns}
            data={productosFiltrados}
            pagination
            itemsPerPage={20}
          />
        )}
      </Card>
      </>
      )}

      {modalAbierto && (
        <ListasPreciosModal
          isOpen={modalAbierto}
          onClose={() => {
            setModalAbierto(false);
            setProductoSeleccionado(null);
          }}
          producto={productoSeleccionado}
          onUpdate={handleProductoActualizado}
        />
      )}

      {mostrarVistaImpresion && (
        <ListaPreciosImprimir
          productos={productosParaExport}
          categorias={categorias}
          listaInicial={listaExport}
          onClose={() => setMostrarVistaImpresion(false)}
        />
      )}
    </div>
  );
};

export default GestionPrecios;
